/**
 * SbN Review — client search + list.
 *
 * Route: /sbn
 *
 * Read-only browsing of Strength by Numbers clients. Search by name,
 * click a row to open the dedicated client detail page.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Activity, Search, Loader2, AlertCircle, ArrowRight, Settings } from "lucide-react";

function fmtDT(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}
function fmtD(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(); } catch { return String(v); }
}

export default function SbnReview() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ configured: null });

  useEffect(() => {
    api.get("/sbn/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ configured: false }));
  }, []);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/sbn/clients", { params: { q: q.trim(), take: 25 } });
        if (!cancelled) setRows(r.data?.clients || []);
      } catch (e) {
        if (!cancelled) toast.error("SbN search failed", {
          description: e?.response?.data?.detail || e.message,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  if (status.configured === false) {
    return (
      <div className="space-y-6" data-testid="sbn-review-page">
        <Header />
        <Card className="p-8 rounded-lg border border-amber-200 bg-amber-50 text-amber-900">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold">Strength by Numbers is not configured</div>
              <p className="text-sm mt-1 text-amber-800/90 max-w-xl">
                Save your SbN GraphQL API key in Settings to start reviewing
                assessment data here. Ask your Axit Connect / SbN admin
                console for the key.
              </p>
              <Button
                className="mt-4 bg-zinc-950 hover:bg-zinc-800 text-white"
                onClick={() => navigate("/settings")}
                data-testid="sbn-review-goto-settings"
              >
                <Settings className="h-4 w-4 mr-1.5" /> Open Settings
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sbn-review-page">
      <Header />
      <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-lg">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search SbN clients by name (min. 2 chars)…"
              className="pl-9 border-zinc-300"
              autoFocus
              data-testid="sbn-review-search"
            />
          </div>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
          <div className="ml-auto text-xs text-zinc-500 font-mono-num">
            {rows.length} {rows.length === 1 ? "match" : "matches"}
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="sbn-review-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-4">Client</th>
                <th className="py-2 pr-4">DOB</th>
                <th className="py-2 pr-4">Gender</th>
                <th className="py-2 pr-4 text-right">Sessions</th>
                <th className="py-2 pr-4">Last session</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {q.trim().length < 2 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-zinc-500 text-sm">
                    Start typing to search SbN clients.
                  </td>
                </tr>
              )}
              {q.trim().length >= 2 && !loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-zinc-500 text-sm">
                    No matches. Try a different name or check the client
                    exists in SbN.
                  </td>
                </tr>
              )}
              {rows.map((c) => {
                const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
                return (
                  <tr
                    key={c.client_id}
                    className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                    onClick={() => navigate(`/sbn/clients/${encodeURIComponent(c.client_id)}`)}
                    data-testid={`sbn-review-row-${c.client_id}`}
                  >
                    <td className="py-3 pr-4">
                      <div className="font-medium text-zinc-950">{name}</div>
                      {c.email && (
                        <div className="text-2xs text-zinc-500 font-mono-num">{c.email}</div>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-xs text-zinc-600 font-mono-num">
                      {fmtD(c.date_of_birth)}
                    </td>
                    <td className="py-3 pr-4 text-xs text-zinc-600 capitalize">
                      {c.gender || "—"}
                    </td>
                    <td className="py-3 pr-4 text-right font-mono-num">
                      {c.sessionCount ?? 0}
                    </td>
                    <td className="py-3 pr-4 text-xs text-zinc-600 font-mono-num">
                      {fmtDT(c.lastSession?.time)}
                    </td>
                    <td className="py-3 pr-4 text-right">
                      <Button
                        size="sm" variant="outline"
                        className="border-zinc-300"
                      >
                        Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
        Clients · Strength by Numbers
      </div>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 flex items-center gap-3">
        <Activity className="h-8 w-8" /> SbN Review
      </h1>
      <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
        Browse assessment sessions pulled live from your Strength by Numbers
        (Axit Connect) account. Search a client, drill into any session,
        compare two sessions side-by-side, and export results as CSV.
      </p>
      <div className="mt-2">
        <Badge className="bg-zinc-100 text-zinc-700 border border-zinc-200 text-2xs uppercase tracking-wider">
          Read-only · live GraphQL
        </Badge>
      </div>
    </div>
  );
}
